import React, { useState, useEffect } from 'react'; 
import { supabase } from '../supabaseClient';
import { useSearch } from './AdminSearchContext';

function AdminProductAdd() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const { searchTerm } = useSearch();
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState({
    name: '',
    price: '',
    description: '',
    image: null,
    category: '' 
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const filteredProducts = products.filter(product => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      product.name.toLowerCase().includes(term) ||
      product.description.toLowerCase().includes(term) ||
      product.price.toString().includes(term) ||
      (product.category && product.category.toLowerCase().includes(term)) 
    );
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('id', { ascending: false });

      if (error) throw error;
      setProducts(data);
    } catch (error) {
      console.error('Erreur:', error);
      setError(error.message);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm(prev => ({ ...prev, [name]: files ? files[0] : value }));
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
    const filePath = `products/${fileName}`;

    const { error } = await supabase.storage.from('product-images').upload(filePath, file);
    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage.from('product-images').getPublicUrl(filePath);
    return publicUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) throw new Error('Vous devez être connecté');

      let imageUrl = editingProduct ? editingProduct.image_url : '';
      if (form.image) {
        imageUrl = await uploadImage(form.image);
      }

      if (editingProduct) {
        const { error } = await supabase
          .from('products')
          .update({
            name: form.name,
            price: parseFloat(form.price),
            description: form.description,
            image_url: imageUrl,
            category: form.category, 
            user_id: user.id
          })
          .eq('id', editingProduct.id);

        if (error) throw error;
        setSuccess('Produit modifié avec succès');
      } else {
        const { error } = await supabase
          .from('products')
          .insert([{
            name: form.name,
            price: parseFloat(form.price),
            description: form.description,
            image_url: imageUrl,
            category: form.category,
            user_id: user.id
          }]);

        if (error) throw error;
        setSuccess('Produit ajouté avec succès');
      }

      resetForm();
      fetchProducts();
      setCurrentPage(1); 
    } catch (error) {
      console.error('Erreur:', error);
      setError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setForm({ name: '', price: '', description: '', image: null, category: '' }); 
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setForm({
      name: product.name,
      price: product.price,
      description: product.description,
      image: null,
      category: product.category || ''   
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Confirmer la suppression du produit ?')) {
      try {
        const { error } = await supabase.from('products').delete().eq('id', id);
        if (error) throw error;
        setSuccess('Produit supprimé avec succès');
        fetchProducts();
      } catch (error) {
        console.error('Erreur:', error);
        setError(error.message);
      }
    }
  };

  return (
    <div className="content-wrapper">
      <div className="page-header">
        <h3 className="page-title">Gestion des produits</h3>
         <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item" style={{color: "#0063cc"}}>Tables</li>
            <li className="breadcrumb-item active" aria-current="page">Produits</li>
          </ol>
        </nav>
      </div>

      <div className="row">
        <div className="col-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">{editingProduct ? 'Modifier' : 'Ajouter'} un produit</h4>
              <form className="forms-sample" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Nom</label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Prix (€)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="price"
                    step="1"
                    min="0"
                    value={form.price}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    rows="4"
                    value={form.description}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Catégorie</label>
                  <select
                    className="form-control"
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Choisir une catégorie --</option>
                    <option value="Huile">Huile</option>
                    <option value="Miel">Miel</option>
                    <option value="Savon">Savon</option>
                  </select>
                </div>

                <div className="form-group">
  <label>Image du produit</label>
  <div className="file-input-container">
    <input
      type="file"
      className="file-input"
      id="productImage"
      name="image"
      accept="image/*"
      onChange={handleChange}
    />
    <label htmlFor="productImage" className="file-input-label">
      <span className="file-input-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="17 8 12 3 7 8"></polyline>
          <line x1="12" y1="3" x2="12" y2="15"></line>
        </svg>
      </span>
      <span className="file-input-text">
        {form.image ? form.image.name : "Choisir un fichier"}
      </span>
    </label>
    {form.image && (
      <button 
        type="button" 
        className="file-remove-btn"
        onClick={() => setForm(prev => ({ ...prev, image: null }))}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    )}
  </div>
  {editingProduct && editingProduct.image_url && (
    <div className="mt-2">
      <span>Image actuelle: </span>
      <img 
        src={editingProduct.image_url} 
        alt="Prévisualisation" 
        style={{ maxWidth: '100px', maxHeight: '100px' }} 
        className="mt-1" />
    </div>
  )}
</div>
                <button type="submit" className="btn btn-primary mr-2" disabled={uploading}>
                  {uploading ? "En cours..." : (editingProduct ? "Modifier" : "Ajouter")}
                </button>
                {editingProduct && (
                  <button type="button" className="btn btn-dark" onClick={resetForm}>
                    Annuler
                  </button>
                )}
                {error && <div className="alert alert-danger mt-3">❌ {error}</div>}
                {success && <div className="alert alert-success mt-3">✅ {success}</div>}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des produits */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">Liste des produits</h4>
              <div className="table-responsive custom-scroll">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Prix (€)</th>
                      <th>Description</th>
                      <th>Catégorie</th> 
                      <th>Image</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentProducts.map(product => (
                      <tr key={product.id}>
                        <td>{product.name}</td>
                        <td>{product.price}</td>
                        <td>{product.description}</td>
                        <td>{product.category}</td> 
                        <td>
                          {product.image_url && (
                            <img src={product.image_url} alt="Produit" style={{ maxWidth: '100px' }} />
                          )}
                        </td>{/*className="btn btn-sm btn-info mr-2"*/}
                        <td>
                          <button onClick={() => {handleEdit(product); scrollToTop();}} className="btn btn-sm btn-primary mr-2">
                            Modifier
                          </button>
                          <button onClick={() => handleDelete(product.id)} className="btn btn-sm btn-danger">
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredProducts.length === 0 && (
                      <tr>
                        <td colSpan="6">Aucun produit disponible</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

                {filteredProducts.length > 0 && (
                  <nav aria-label="Page navigation" className="mt-4">
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={goToPreviousPage} aria-label="Previous">
                          <span aria-hidden="true">&laquo;</span>
                        </button>
                      </li>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => paginate(page)}>
                            {page}
                          </button>
                        </li>
                      ))}
                      
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={goToNextPage} aria-label="Next">
                          <span aria-hidden="true">&raquo;</span>
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminProductAdd;